/**
 * Single source of truth for the matter timeline shown to both staff and
 * clients. Steps are defined here, "done" state is computed from a context
 * object containing matter + intake forms + docs + payments. Callers (the
 * staff LodgementGate, the client MatterOverview, the Dashboard) all render
 * from the same data, so the timeline can never disagree with itself.
 */
import {
  STATUS_AWAITING_REVIEW,
  STATUS_AWAITING_FINAL_REVIEW,
  STATUS_READY_FOR_LODGEMENT,
  STATUS_LODGED,
  STATUS_APPROVED,
  STATUS_REFUSED,
  STATUS_WITHDRAWN,
} from './matterStatus';

export interface TimelineContext {
  matter: {
    status: string;
    initial_payment_date: string | null;
    final_payment_date: string | null;
    /** Used as the "Contract signed" timestamp. */
    created_at?: string | null;
  };
  applicantIntakeStatus: string | null;
  applicantIntakeCompletedAt: string | null;
  /** True only if this visa requires a sponsor intake (passed in from caller).
      Drives whether the second-party step is shown and its status fetched. */
  sponsorIntakeRequired: boolean;
  /** True only when an unfinished second-party intake should BLOCK lodgement.
      Sponsor forms (40SP, 1149) are lodged with the Department, so they block.
      The 600-tourist Inviter form is ADI's own and is never lodged, so it is
      shown on the timeline but never gates lodgement (Tzvi ruling 2026-07-24). */
  sponsorIntakeBlocksLodgement?: boolean;
  /** Sponsor form number shown in sponsor-intake copy: '1149' for 600-family,
      '40SP' for partner visas. Defaults to '40SP' when unset. Pass null for the
      600-tourist Inviter, whose ADI-authored form has no Department number. */
  sponsorFormNumber?: string | null;
  /** Client-facing noun for the second party: 'inviter' on the 600 tourist
      stream, 'sponsor' everywhere else. Defaults to 'sponsor'. */
  sponsorRoleNoun?: 'sponsor' | 'inviter';
  sponsorIntakeStatus: string | null;
  sponsorIntakeCompletedAt: string | null;
  /** Required-doc roll-ups for the "Documents collected" / "Documents approved" steps. */
  requiredDocsTotal: number;
  requiredDocsUploaded: number;
  requiredDocsApproved: number;
}

export type StepStatus = 'done' | 'current' | 'pending';

export interface TimelineStep {
  id: string;
  staffLabel: string;
  staffDesc: string;
  clientLabel: string;
  clientDesc: string;
  /** Date stamp shown under the step when known. */
  date: string | null;
  status: StepStatus;
  /** Hidden steps drop out of the array entirely (e.g. sponsor intake on no-sponsor visas). */
  hidden?: boolean;
  /** When set, clicking this step (staff mode) transitions matter.status to
      this value. Steps without a target are read-only — they're derived from
      data (payments, intake completion, etc.) so there's no manual override. */
  targetStatus?: string;
}

const INTAKE_DONE = new Set(['completed', 'submitted']);

/**
 * Build the full timeline for a matter. Steps are evaluated in order; the
 * first step that's not done becomes the "current" one. Anything past it is
 * pending.
 */
export function computeTimelineSteps(ctx: TimelineContext): TimelineStep[] {
  const m = ctx.matter;
  const status = m.status;
  const sponsorRoleNoun = ctx.sponsorRoleNoun ?? 'sponsor';
  const sponsorRoleLabel = sponsorRoleNoun === 'inviter' ? 'Inviter' : 'Sponsor';
  // The Inviter form is ADI's own, so there is no Department form number to
  // quote; `sponsorFormNumber: null` suppresses the parenthetical.
  const sponsorFormNumber =
    ctx.sponsorFormNumber === null ? null : ctx.sponsorFormNumber ?? '40SP';
  const sponsorFormSuffix = sponsorFormNumber ? ` (${sponsorFormNumber})` : '';

  // Status reach helpers — true once the matter has reached a given phase
  // (so a Lodged matter is "past" Awaiting Review etc.).
  const reached = (target: string): boolean => {
    const order = [
      'Contract',          // sentinel — always reached
      STATUS_AWAITING_REVIEW,
      STATUS_AWAITING_FINAL_REVIEW,
      STATUS_READY_FOR_LODGEMENT,
      STATUS_LODGED,
      STATUS_APPROVED,
    ];
    const ti = order.indexOf(target);
    const ci = Math.max(order.indexOf(status), 0);
    // Approved overrides everything below it. Refused/Withdrawn = terminal but
    // doesn't claim "approved".
    if (ti < 0) return false;
    return ci >= ti;
  };

  const applicantIntakeDone = ctx.applicantIntakeStatus !== null && INTAKE_DONE.has(ctx.applicantIntakeStatus);
  const sponsorIntakeDone = !ctx.sponsorIntakeRequired
    || (ctx.sponsorIntakeStatus !== null && INTAKE_DONE.has(ctx.sponsorIntakeStatus));
  const docsAllUploaded = ctx.requiredDocsTotal > 0 && ctx.requiredDocsUploaded === ctx.requiredDocsTotal;
  const docsAllApproved = ctx.requiredDocsTotal > 0 && ctx.requiredDocsApproved === ctx.requiredDocsTotal;

  const isApproved = status === STATUS_APPROVED;
  const isRefused = status === STATUS_REFUSED;
  const isWithdrawn = status === STATUS_WITHDRAWN;

  // Done predicates per step. Order matters — first not-done is "current".
  const raw: Array<Omit<TimelineStep, 'status'> & { done: boolean }> = [
    {
      id: 'contract',
      staffLabel: 'Contract signed',
      staffDesc: 'Engagement letter accepted; matter created.',
      clientLabel: 'Contract signed',
      clientDesc: 'You\'ve agreed to ADI\'s engagement terms.',
      date: m.created_at ?? null,
      done: true,
    },
    {
      id: 'initial_payment',
      staffLabel: 'Initial payment received',
      staffDesc: 'First instalment received from the client.',
      clientLabel: 'Initial payment received',
      clientDesc: 'We\'ve received your first payment.',
      date: m.initial_payment_date,
      done: !!m.initial_payment_date,
    },
    {
      id: 'applicant_intake',
      staffLabel: 'Applicant intake form completed',
      staffDesc: 'Applicant has filled in their schema-driven intake form.',
      clientLabel: 'Complete your intake form',
      clientDesc: 'Fill in your details: citizenship, family, relationship history.',
      date: ctx.applicantIntakeCompletedAt,
      done: applicantIntakeDone,
    },
    {
      id: 'sponsor_intake',
      staffLabel: `${sponsorRoleLabel} intake form completed`,
      staffDesc: `${sponsorRoleLabel} has filled in their ${sponsorFormNumber ?? sponsorRoleNoun} form.`,
      clientLabel: `${sponsorRoleLabel} completes their form`,
      clientDesc:
        sponsorRoleNoun === 'inviter'
          ? 'Your inviter fills in their own short form.'
          : `Your sponsor fills in the sponsorship form${sponsorFormSuffix}.`,
      date: ctx.sponsorIntakeCompletedAt,
      done: sponsorIntakeDone,
      hidden: !ctx.sponsorIntakeRequired,
    },
    {
      id: 'documents_collected',
      staffLabel: 'Documents uploaded',
      staffDesc: ctx.requiredDocsTotal > 0
        ? `${ctx.requiredDocsUploaded} of ${ctx.requiredDocsTotal} required documents uploaded.`
        : 'Required documents uploaded.',
      clientLabel: 'Upload your documents',
      clientDesc: 'Upload everything on the documents checklist (passports, police checks, photos…).',
      date: null,
      done: docsAllUploaded,
    },
    {
      id: 'cm_review',
      staffLabel: 'Case manager review',
      staffDesc: 'Case manager checking each document, marking approved or needs replacement.',
      clientLabel: 'Your case manager reviews everything',
      clientDesc: 'We check each document and may ask you to replace some.',
      date: null,
      done: docsAllApproved || reached(STATUS_AWAITING_FINAL_REVIEW),
      targetStatus: STATUS_AWAITING_REVIEW,
    } as Omit<TimelineStep, 'status'> & { done: boolean },
    {
      id: 'final_review',
      staffLabel: "Eli's final review",
      staffDesc: 'Reviewer (Eli) doing the final check before lodgement.',
      clientLabel: 'Final review by your migration agent',
      clientDesc: 'Your migration agent does one last review of the whole application.',
      date: null,
      done: reached(STATUS_AWAITING_FINAL_REVIEW) && (reached(STATUS_READY_FOR_LODGEMENT) || reached(STATUS_LODGED) || isApproved),
      targetStatus: STATUS_AWAITING_FINAL_REVIEW,
    } as Omit<TimelineStep, 'status'> & { done: boolean },
    {
      id: 'ready_for_lodgement',
      staffLabel: 'Ready for lodgement',
      staffDesc: 'Application has been approved. Lodge with Home Affairs when the timing is right.',
      clientLabel: 'Application ready to lodge',
      clientDesc: 'Your migration agent has approved the application; it\'s queued for lodgement.',
      date: null,
      done: reached(STATUS_READY_FOR_LODGEMENT) && (reached(STATUS_LODGED) || isApproved),
      targetStatus: STATUS_READY_FOR_LODGEMENT,
    } as Omit<TimelineStep, 'status'> & { done: boolean },
    {
      id: 'final_invoice',
      staffLabel: 'Final invoice paid',
      staffDesc: 'Final balance received from the client.',
      clientLabel: 'Final invoice paid',
      clientDesc: 'You\'ve paid the final instalment.',
      date: m.final_payment_date,
      done: !!m.final_payment_date,
    },
    {
      id: 'lodged',
      staffLabel: 'Lodged with Home Affairs',
      staffDesc: 'Application submitted. Awaiting decision.',
      clientLabel: 'Lodged with Home Affairs',
      clientDesc: 'Your application has been submitted to Home Affairs.',
      date: null,
      done: reached(STATUS_LODGED) || isApproved,
      targetStatus: STATUS_LODGED,
    } as Omit<TimelineStep, 'status'> & { done: boolean },
    {
      id: 'approved',
      staffLabel: 'Approved',
      staffDesc: 'Visa granted. Matter complete.',
      clientLabel: 'Approved',
      clientDesc: 'Your visa has been granted. Congratulations!',
      date: null,
      done: isApproved,
      targetStatus: STATUS_APPROVED,
    } as Omit<TimelineStep, 'status'> & { done: boolean },
  ];

  const visible = raw.filter((s) => !s.hidden);

  // First not-done = current. If everything's done, no "current" (matter is
  // approved). Refused/withdrawn flag the terminal step they reached as
  // "current" rather than "done".
  let firstPendingIdx = visible.findIndex((s) => !s.done);
  if (firstPendingIdx < 0) firstPendingIdx = visible.length; // all done

  const result: TimelineStep[] = visible.map((s, i) => ({
    ...s,
    status: i < firstPendingIdx ? 'done' : i === firstPendingIdx ? 'current' : 'pending',
  }));

  // Refused/withdrawn: append a synthetic terminal step so the client sees
  // the outcome rather than the matter just stopping silently.
  if (isRefused || isWithdrawn) {
    const terminalLabel = isRefused ? 'Refused' : 'Withdrawn';
    result.push({
      id: 'terminal',
      staffLabel: terminalLabel,
      staffDesc: isRefused
        ? 'Application refused by Home Affairs.'
        : 'Application withdrawn.',
      clientLabel: terminalLabel,
      clientDesc: isRefused
        ? 'Your application was refused. Your migration agent will be in touch.'
        : 'Your application was withdrawn.',
      date: null,
      status: 'current',
    });
  }

  return result;
}
